import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import NewRound from "@/pages/NewRound";
import ScoreEntry from "@/pages/ScoreEntry";
import Rounds from "@/pages/Rounds";
import RoundDetail from "@/pages/RoundDetail";
import Stats from "@/pages/Stats";
import Leaderboard from "@/pages/Leaderboard";
import Friends from "@/pages/Friends";
import GroupRoundPlay from "@/pages/GroupRoundPlay";
import ShareRound from "@/pages/ShareRound";
import Tournaments from "@/pages/Tournaments";
import TournamentDetail from "@/pages/TournamentDetail";
import Notifications from "@/pages/Notifications";
import Pricing from "@/pages/Pricing";
import PricingSuccess from "@/pages/PricingSuccess";
import Booking from "@/pages/Booking";
import ResortDetail from "@/pages/ResortDetail";
import MyBookings from "@/pages/MyBookings";
import AdvancedStats from "@/pages/AdvancedStats";
import ClubBag from "@/pages/ClubBag";
import Referrals from "@/pages/Referrals";
import Admin from "@/pages/Admin";
import { Terms, Privacy, Contact } from "@/pages/Legal";
import ClubAverages from "@/pages/ClubAverages";
import Profile from "@/pages/Profile";
import Landing from "@/pages/Landing";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MyBag from "@/pages/MyBag";
import CourseFlyover from "@/pages/CourseFlyover";
import Help from "@/pages/Help";
import Badges from "@/pages/Badges";
import "@/App.css";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#0F2537]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/courses" element={<Protected><Courses /></Protected>} />
          <Route path="/courses/:id" element={<Protected><CourseDetail /></Protected>} />
          <Route path="/rounds/new" element={<Protected><NewRound /></Protected>} />
          <Route path="/rounds/play/:courseId" element={<Protected><ScoreEntry /></Protected>} />
          <Route path="/rounds" element={<Protected><Rounds /></Protected>} />
          <Route path="/rounds/:id" element={<Protected><RoundDetail /></Protected>} />
          <Route path="/stats" element={<Protected><Stats /></Protected>} />
          <Route path="/stats/advanced" element={<Protected><AdvancedStats /></Protected>} />
          <Route path="/stats/clubs" element={<Protected><ClubBag /></Protected>} />
          <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
          <Route path="/friends" element={<Protected><Friends /></Protected>} />
          <Route path="/group-rounds/:id" element={<Protected><GroupRoundPlay /></Protected>} />
          <Route path="/share/:kind/:id" element={<ShareRound />} />
          <Route path="/tournaments" element={<Protected><Tournaments /></Protected>} />
          <Route path="/tournaments/:id" element={<Protected><TournamentDetail /></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
          <Route path="/booking" element={<Protected><Booking /></Protected>} />
          <Route path="/resorts/:id" element={<Protected><ResortDetail /></Protected>} />
          <Route path="/bookings" element={<Protected><MyBookings /></Protected>} />
          <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
          <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
          <Route path="/admin" element={<Protected><Admin /></Protected>} />
          <Route path="/pricing/success" element={<Protected><PricingSuccess /></Protected>} />
          <Route path="/pro/club-averages" element={<Protected><ClubAverages /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/bag" element={<Protected><MyBag /></Protected>} />
          <Route path="/courses/:courseId/flyover" element={<Protected><CourseFlyover /></Protected>} />
          <Route path="/help" element={<Protected><Help /></Protected>} />
          <Route path="/badges" element={<Protected><Badges /></Protected>} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <PWAInstallPrompt />
    </AuthProvider>
  );
}
